import ImageHeader from "@arelia-components/layout/ImageHeader";
import { Avatar, Box } from "@mui/material";
import { GridLayout } from "@arelia-components/layout/GridLayout";
import {
  CustomerInfoContainer,
  Status,
} from "./components";
import { CustomerDetails, CustomerStatus } from "../models";
import RoleChip from "@arelia-components/ui-atoms/RoleChip";
import LabelDetailsContainer from "@arelia-components/LabelDetails/LabelDetailsContainer";
import SocialsContainer from "@arelia-components/Socials/SocialsContainer";
import MembersContainer from "@arelia-components/Members/MembersContainer";
import theme from "src/theme";
import SubscriptionDetailsContainer from "@arelia-components/SubscriptionDetails/SubscriptionDetailsContainer";
import PaymentHistoryContainer from "@arelia-components/PaymentHistory/PaymentHistoryContainer";
import CloseUser from "./components/CloseUser/CloseUser";
import { DeepPartial } from "@arelia-types/index";
import { CxActivityHistoryContainer } from "./components/ActivityHistory/CxActivityHistoryContainer";
import DeleteUser from "./components/DeleteUser/DeleteUser";

type Props = {
  customerDetails: CustomerDetails;
  updateCustomer: (cutomerUpdates: DeepPartial<CustomerDetails>) => void;
}

export function CustomerDetailsPresenter({ customerDetails, updateCustomer }: Props) {
  const title =
    <Box sx={styles.titleBox}>
      <Box sx={styles.title}>
        <Avatar src={customerDetails.avatarUrl} sx={styles.titleAvatar} />
        {customerDetails.band?.name}
        <RoleChip role={customerDetails.role} size="medium" sx={styles.roleChip}/>
      </Box>
      <Box display="flex">
        <CloseUser
          identityId={customerDetails.identityId}
          status={customerDetails.status}
          role={customerDetails.role}
          plan={customerDetails.subscription?.plan}
        />
        <DeleteUser
          identityId={customerDetails.identityId}
          disabled={customerDetails.status !== CustomerStatus.Closed}
        />
      </Box>
    </Box>
  ;

  return (
    <Box sx={styles.wrapperBox}>
      <ImageHeader
        title={title}
        backTo={{
          label: "back to customers",
          path: "/hq/customers"
        }}
        sx={styles.imageHeaderSx(customerDetails.avatarUrl)}
        dashboard
      ></ImageHeader>
      <Box p={5} pt={2}>
        <GridLayout>
          <Status status={customerDetails.status} createdDate={customerDetails.createdDate} />
          <LabelDetailsContainer bandName={customerDetails.band?.name} location={customerDetails.band?.location} update={updateCustomer}/>
          <SocialsContainer social={customerDetails.customerInfo?.social || []} update={updateCustomer} />

          <SubscriptionDetailsContainer />
          <PaymentHistoryContainer />

          <CustomerInfoContainer customerDetails={customerDetails} updateCustomer={updateCustomer} />
          <MembersContainer members={customerDetails.band?.members || []} update={updateCustomer} />

          <CxActivityHistoryContainer />
        </GridLayout>
      </Box>
    </Box>
  );
}

const styles = {
  wrapperBox: {
    bgcolor: theme.palette.background.default,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '34px',
    overflowY: 'auto',
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "20px",
    width: "100%"
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: "20px"
  },
  titleAvatar: {
    width: "62px",
    height: "62px",
    borderRadius: "30px"
  },
  imageHeaderSx(avatarUrl: string) {
    return {
      background: "none",
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: "-35px",
        left: "-35px",
        right: 0,
        bottom: 0,
        background: `url(${avatarUrl}) no-repeat`,
        backgroundPosition: 'center',
        backgroundSize: '130% auto',
        filter: 'blur(25px)',
        zIndex: 0
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: "0",
        left: "0",
        right: 0,
        bottom: 0,
        background: "linear-gradient(154deg, #00121C 14.26%, #00475E 83.68%)",
        opacity: 0.6,
        zIndex: 1
      }
    };
  },
  roleChip: {
    '& .MuiChip-label': {
      fontSize: '14px'
    }
  }
}